<script setup lang="ts">
  import { defineAsyncComponent,ref,computed,onMounted,watch,nextTick,inject } from 'vue';
  import { Category } from '@/models/responseMenu.interface';
  import TrackService from "@/services/track-custom";
  import { storeToRefs } from 'pinia';
  import { useMenuStore } from "@/store";
  import { useRoute, useRouter } from 'vue-router';
  import { useClickEvent } from '@/helpers/composables';

  interface RouteParams {
    category_id?: string;
    subcategory_id?: string;
  };
  interface ProductListByCategoryMethods {
    setAllCategoriesWithProducts: (cateogire:Category) => void;
    getProductsCategories: (cateogire:Category) => void;
    getAllProductsCategories: (cateogire:Category) => void;
  };
  interface recommendedChildMethods {
    getProductsContentHome: () => void;
  };

  const MoleculeHeaderSliderCategories = defineAsyncComponent(() => import("@/components/molecules/MoleculeHeaderSliderCategories.vue"));
  const MoleculeProductListByCategory = defineAsyncComponent(() => import('@/components/molecules/MoleculeProductListByCategory.vue'));
  const MoleculeAllCategories = defineAsyncComponent(() => import('@/components/molecules/MoleculeAllCategories.vue'));
  const MoleculeRecommended = defineAsyncComponent(() => import('@/components/molecules/MoleculeRecommended.vue'));
  const AlertGlobe = defineAsyncComponent(()=> import("@/components/alert/AlertGlobe.vue"));

  const props = defineProps({
    currentSlide: Number,
    divElementId: String,
    flatNearDivLimit:Boolean,
    flatButtonDiv: Boolean,
    scrollPosition: Number
  });

  const menuStore = useMenuStore();
  const route = useRoute();
  const router = useRouter();
  const {
    menuData,addAllCategoriesOnStoriesMode,
    flatHomeAndStorieMode,selectedSubCategory,showFootNavCategories,
    currentCategory,subCategories,mainCategories,hasChanged
  } = storeToRefs(menuStore);
  const { findCategory,deleteCategorieMain,addCategoriesToMain,clickCategory } = useClickEvent();

  let flatSubCategories = ref<boolean>(false);
  const productListByCategoryChild = ref<null | (ProductListByCategoryMethods & InstanceType<typeof MoleculeProductListByCategory>)>(null);
  const recommendedChild = ref<null | (recommendedChildMethods & InstanceType<typeof MoleculeRecommended>)>(null);
  const methodQueue: any[] = [];
  const childMethodsLoaded = ref(false);
  let languageChangeAlert = ref<Boolean>(true);

  const listProductsCategories = inject('listProductsCategories', ref());

  const emit = defineEmits([ "slideTo","handleTouchStart","handleTouchEnd" ]);

  const isHome = computed(() => (route.name === 'RecommendedCategory' || route.name === 'Menu'));
  const isAllCategory = computed(() => (route.name === 'AllCategory'));
  const modeStoriesActice = computed(() => (menuData.value && !menuData.value.content_home?.length || addAllCategoriesOnStoriesMode.value));
  const isHomeDontRecommended = computed(() => isHome.value && menuData.value?.content_home?.length === 0);
  const getCategorieID = computed(() => {
    if (menuData.value?.content_home.length && isHome.value) return 1;
    if (menuData.value?.content_home.length === 0 && addAllCategoriesOnStoriesMode.value && isHome.value) return 0;
    return null;
  });
  const routeParamsCategoryId = computed(() => route.params.category_id as string);
  const hasMultilenguage = computed(() => menuData.value.cluvi_add_ons.includes('multilanguage'));

  watch(productListByCategoryChild, async (newValue) => {
    if (newValue) {
      await nextTick();
      executeQueue();
      childMethodsLoaded.value = true;
    };
  });
  watch(currentCategory,(val) => {
    if (childMethodsLoaded.value && val) executeQueue();
  });

  const setMainCategories = (categories: Category[]) => {
    mainCategories.value = categories;
    mainCategories.value = deleteCategorieMain(mainCategories.value);
    mainCategories.value = addCategoriesToMain(mainCategories.value);
    if (modeStoriesActice.value) enqueueMethod('setAllCategoriesWithProducts',mainCategories.value[0]);
    if (isHomeDontRecommended.value) clickCategory(categories[0]);
    checkRouteParams(mainCategories.value);
  };
  const checkRouteParams = (categories: Category[]) => {
    const { category_id, subcategory_id }: RouteParams = route.params;
    const categoryId = getCategoryID({ category_id, subcategory_id });
    const subcategoryId = getSubcategoryID({ category_id, subcategory_id });

    if (categoryId) loadCategory(categories, categoryId);
    if (subcategoryId) loadSubcategory(subcategoryId);
    if (!categoryId && !subcategoryId) loadDefaultCategory(categories);
  };
  const getCategoryID = (routeParams: RouteParams): number => {
    return routeParams.category_id ? Number(routeParams.category_id) : 0;
  };
  const getSubcategoryID = (routeParams: RouteParams): number => {
    return routeParams.subcategory_id ? Number(routeParams.subcategory_id) : 0;
  };
  const loadCategory = (categories: Category[], categoryId: number) => {
    const category = findCategory(categories, categoryId);
    if (category) {
      document.title = category.label;
      categorieData(category);
    } else {
      router.push({ name: 'notFound' });
    };
  };
  const loadSubcategory = (subcategoryId: number) => {
    setTimeout(() => goToSubCategorie(subcategoryId.toString()), 1000);
  };
  const loadDefaultCategory = (categories: Category[]) => {
    const defaultCategoryId = getCategorieID.value;
    currentCategory.value = defaultCategoryId ? mainCategories.value.find((element) => element.id === defaultCategoryId) : categories[0];
    categorieData(currentCategory.value as Category);
  };
  const categorieData = (categorie: Category) => {
    currentCategory.value = categorie as Category;
    let isSubCategorie = categorie?.id > 1 && categorie?.type === 'SupplierCategory' ? true : false;
    let isMainCategorie = categorie?.main_category_id && categorie.main_category_id > 1 ? mainCategories.value.find(element => element.id === categorie.main_category_id) : categorie;
    if (isSubCategorie) setTimeout(() => goToSubCategorie(categorie.id.toString()),500);
    if (categorie.id === 1 && recommendedChild.value) recommendedChild.value.getProductsContentHome();
    processMainCategories(isMainCategorie as Category);
  };
  const goToSubCategorie = (categorieID: string) => {
    let displacement:number = 0;
    let elementalertWrap = document.querySelector('.alert-wrap');
    let elementTitle = document.querySelector('.banner-wrap .title');
    if (!elementTitle && elementalertWrap || elementTitle && !elementalertWrap) displacement = 116;
    if (!elementTitle && !elementalertWrap) displacement = 87;
    const miDivSubCategorie = document.getElementById(categorieID);
    if (miDivSubCategorie) {
      const yOffset = miDivSubCategorie.getBoundingClientRect().top;
      const y = window.pageYOffset + yOffset - displacement;
      window.scrollTo({ top: y, behavior: 'smooth' });
    };
  };
  const processMainCategories = (isMainCategorie: Category) => {
    if (isMainCategorie?.subcategories?.length) setSubCategoriesMain(isMainCategorie);
    else setSubCatsMainCategoriyId(isMainCategorie);
    if (isMainCategorie.id === 0 && isMainCategorie.main_category_id === 1 && !modeStoriesActice.value) enqueueMethod('setAllCategoriesWithProducts',isMainCategorie);
    if (isMainCategorie.id === 1 && !modeStoriesActice.value) flatHomeAndStorieMode.value = false;
    handleCategoryProducts(isMainCategorie);
  };
  const handleCategoryProducts = (categorie: Category) => {
    const hasProductsIds = (categorie.product_ids && categorie.product_ids.length) ? true : false;
    if (hasProductsIds) {
      TrackService.TrackEventCluvi({ subcategory_id: categorie.id }, 'set category');
      enqueueMethod('getProductsCategories',categorie);
    };
  };
  const setSubCategoriesMain = (categorie: Category) => {
    subCategories.value = categorie.subcategories;
    enqueueMethod('getAllProductsCategories',subCategories.value);
    flatSubCategories.value = true;
    TrackService.TrackEventCluvi({ maincategory_id: categorie.id }, 'click main category');
  };
  const setSubCatsMainCategoriyId = (categorie: Category) => {
    if (categorie?.main_category_id === null) {
      resetSubCategories();
    } else {
      trackEvent(categorie);
      manageCategoryContent(categorie);
    };
  };
  const resetSubCategories = () => {
    subCategories.value = [];
    flatSubCategoriesFalse();
  };
  const manageCategoryContent = (categorie: Category) => {
    if (!categorie.product_ids?.length) {
      listProductsCategories.value = [];
      if (shouldSetSubCategories(categorie)) {
        setSubCategories(categorie);
        enqueueMethod('getProductsCategories',categorie);
      };
    } else {
      setSubCategories(categorie);
      selectedSubCategory.value = categorie.id;
    };
  };
  const shouldSetSubCategories = (categorie: Category) => {
    return categorie.main_category_id !== 1 && categorie.id !== 0;
  };
  const setSubCategories = (categorie: Category) => {
    subCategories.value = menuData.value.menu.categories.find(mainCat => mainCat.id === categorie.main_category_id)?.subcategories as Category[];
    subCategories.value.length && (subCategories.value = addCategoriesToMain(subCategories.value));
    flatSubCategories.value = true;
  };
  const trackEvent = (categorie: Category) => {
    const eventTracking = categorie.id === 0 ? 'view all products' : categorie.id === 1 ? 'view home' : '';
    if (eventTracking) TrackService.TrackEventCluvi({ maincategory_id: categorie.id }, eventTracking);
  };
  const enqueueMethod = (methodName: string, ...args: any[]) => {
    methodQueue.push({ methodName, args });
  };
  const executeQueue = () => {
      methodQueue.forEach((method) => {
        const { methodName, args } = method;
        if (productListByCategoryChild.value !== null && typeof productListByCategoryChild.value[methodName as keyof ProductListByCategoryMethods] === 'function') (productListByCategoryChild.value[methodName as keyof ProductListByCategoryMethods] as Function)(...args);
      });

      methodQueue.length = 0;
  };
  const flatSubCategoriesFalse = () => {
    flatSubCategories.value = false;
  };
  const slideTo = (val:number) => {
    emit("slideTo",val);
  };
  const handleTouchStart = (event: TouchEvent) => {
    emit('handleTouchStart',event);
  };
  const handleTouchEnd = (event: TouchEvent) => {
    emit('handleTouchEnd',event);
  };
  const hiddenAlertGlobe =  () => {
    languageChangeAlert.value = false;
  };

  defineExpose({ categorieData,flatSubCategoriesFalse });

  onMounted(() => {
    setMainCategories(menuData.value.menu?.categories);
  });
</script>

<template>
  <div v-if="!showFootNavCategories">
    <MoleculeHeaderSliderCategories
      :flatSubCategories="flatSubCategories" :currentSlide="currentSlide"
      :divElementId="divElementId" :flatNearDivLimit="flatNearDivLimit" :flatButtonDiv="flatButtonDiv" :scrollPosition="scrollPosition"
      @categorieData="categorieData" @slideTo="slideTo" @goToSubCategorie="goToSubCategorie" @executeQueue="executeQueue"
    />
    <AlertGlobe v-if="hasMultilenguage && languageChangeAlert" :hasChanged="hasChanged" @hiddenAlertGlobe="hiddenAlertGlobe" />
    <div @touchstart="handleTouchStart" @touchend="handleTouchEnd">
      <MoleculeProductListByCategory
        ref="productListByCategoryChild"
        :isHome="isHome" :routeParamsCategoryId="routeParamsCategoryId" :modeStoriesActice="modeStoriesActice"
        @flatSubCategoriesFalse=flatSubCategoriesFalse
      />
      <MoleculeAllCategories v-if="isAllCategory && (modeStoriesActice || flatHomeAndStorieMode)" />
    </div>
  </div>
  <div @touchstart="handleTouchStart" @touchend="handleTouchEnd">
    <MoleculeRecommended
      ref="recommendedChild"
      :isHome="isHome" :scrollPosition="scrollPosition"
      @resetSubCategories=resetSubCategories @categorieData="categorieData" @slideTo="slideTo"
    />
  </div>
</template>